import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { ThemeService } from '../../../core/theme/theme.service';
import Keycloak from 'keycloak-js';
import { KeycloakService } from 'keycloak-angular';
import { StackedSnackbarService } from '../../../core/services/stacked-snackbar.service';
import { Router } from '@angular/router';
import { WEB_SOCKET_RECEIVE } from '../../../core/constants/web-socket.const';
import { ROLES } from '../../../core/constants/user.const';
import { CustomSocket } from '../../../core/services/custom-socket.service';
import { lastValueFrom, Subject, takeUntil, tap } from 'rxjs';
import { UserService } from '../../../core/services/user.service';

@Component({
  selector: 'dime-toolbar',
  standalone: true,
  imports: [MatToolbarModule, MatButtonModule, MatIconModule, MatBadgeModule, MatMenuModule, MatDividerModule],
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
})
export class ToolbarComponent implements OnInit {
  @Output() menuToggle = new EventEmitter<void>();

  user: any = {
    firstName: 'Cargando...',
    lastName: '',
    avatarUrl: 'assets/images/avatar.png',
  };
  urlAccount = '';

  private destroy$: Subject<void> = new Subject();
  readonly theme = inject(ThemeService);

  private readonly keycloakService = inject(KeycloakService);
  private readonly userService = inject(UserService);
  private readonly socket = inject(CustomSocket);
  private readonly stackedSnackBar = inject(StackedSnackbarService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadUserProfile();
    this.createNotification();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  private async loadUserProfile(): Promise<void> {
    try {
      this.user = await lastValueFrom(this.userService.getUseProfile());
      this.urlAccount = this.keycloakService.getKeycloakInstance().createAccountUrl();
      sessionStorage.setItem('user', JSON.stringify(this.user));
    } catch (error) {
      this.user = { firstName: 'nombre', lastName: 'apellido' };
      console.error(error);
    }
    this.cdr.detectChanges();
  }

  toggleTheme(): void {
    this.theme.toggle();
  }

  onMenuToggle(): void {
    this.menuToggle.emit();
  }

  public logout(): void {
    this.keycloakService.logout();
  }

  private createNotification(): void {
    const eventConfigs = [
      {
        event: WEB_SOCKET_RECEIVE.NEW_USER,
        title: (d: any) => `USUARIO: NUEVO USUARIO`,
        text: (d: any) => `USERNAME: ${d.username}`,
        skipOwnUser: true,
        roles: [],
        excludeUrls: [],
      },
      {
        event: WEB_SOCKET_RECEIVE.UPDATE_USER,
        title: (d: any) => `USUARIO: ACTUALIZACIÓN DE USUARIO`,
        text: (d: any) => `USERNAME: ${d.username}`,
        skipOwnUser: true,
        roles: [],
        excludeUrls: [],
      },
      {
        event: WEB_SOCKET_RECEIVE.NEW_INCIDENT,
        title: (d: any) => `ALERTA: NUEVA ALERTA`,
        text: (d: any) => `Nº: ${d.id} - USERNAME: ${d.username}`,
        skipOwnUser: false,
        roles: [],
        excludeUrls: [],
      },
      {
        event: WEB_SOCKET_RECEIVE.MESSAGE_CREATED,
        title: (d: any) => `ALERTA Nº ${d.incidentId}: NUEVO MENSAJE`,
        text: (d: any) => `${d.body}`,
        skipOwnUser: true,
        roles: [],
        excludeUrls: (d: any) => [`/alerts/detail/${d.incidentId}`],
      },
    ];

    eventConfigs.forEach((config) => {
      this.socket
        .fromEvent(config.event)
        .pipe(takeUntil(this.destroy$))
        .subscribe((response: any) => {
          const currentUrl = this.router.url;
          const data = response;

          if ((config.skipOwnUser && data.username === this.user.username) || data.senderKind === 'operator') return;

          const excludeUrls: string[] = typeof config.excludeUrls === 'function' ? config.excludeUrls(data) : config.excludeUrls;

          if (excludeUrls.length > 0) {
            const shouldExclude = excludeUrls.some((u) => currentUrl.includes(u));
            if (shouldExclude) return;
          }

          if (config.roles.length > 0) {
            const hasRole = config.roles.some((r) => this.keycloakService.isUserInRole(r));
            if (!hasRole) return;
          }

          this.stackedSnackBar.add({
            date: data.createdAt,
            user: data.username,
            title: config.title(data),
            text: config.text(data),
          });
          this.cdr.markForCheck();
        });
    });
  }
}
